import { motion } from 'framer-motion';
import { ArrowRight, ScanBarcode, Store } from 'lucide-react';

const CTA = () => {
  return (
    <section id="contact" className="py-20 md:py-32 bg-gradient-to-br from-brand-secondary to-[#1a1f36] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-accent rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-white mb-6">
              Ready to{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
                skip the queue?
              </span>
            </h2>
            <p className="text-lg md:text-xl font-medium leading-relaxed text-slate-300 mb-8 max-w-lg">
              Join 500,000+ shoppers enjoying a faster checkout experience, 
              or bring GrabPe to your store and delight your customers.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-brand-secondary rounded-full px-8 py-4 font-bold transition-all hover:scale-105 active:scale-95"
                data-testid="cta-download-app-btn"
              >
                <ScanBarcode className="w-5 h-5" />
                Download App
              </a>
              <a
                href="#store-form"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 hover:bg-white/10 text-white rounded-full px-8 py-4 font-semibold transition-all"
                data-testid="cta-for-stores-btn"
              >
                <Store className="w-5 h-5" />
                I'm a Store Owner
              </a>
            </div>

            {/* App Store Badges */}
            <div className="flex flex-wrap items-center gap-4">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/200px-Download_on_the_App_Store_Badge.svg.png" 
                alt="Download on App Store" 
                className="h-10 opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
              />
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/200px-Google_Play_Store_badge_EN.svg.png" 
                alt="Get it on Google Play" 
                className="h-10 opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
              />
            </div>
          </motion.div>

          {/* Right Content - Store Owner Form */}
          <motion.div
            id="store-form"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Store className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-brand-secondary mb-2">
                Bring GrabPe to your store
              </h3>
              <p className="text-neutral-text-secondary">
                Start your free 30-day trial
              </p>
            </div>

            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <div>
                <input
                  type="text"
                  placeholder="Store name"
                  className="w-full bg-neutral-bg-secondary border-transparent focus:bg-white focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/20 rounded-xl px-4 py-4 transition-all outline-none text-brand-secondary placeholder:text-neutral-text-secondary"
                  data-testid="cta-store-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Business email"
                  className="w-full bg-neutral-bg-secondary border-transparent focus:bg-white focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/20 rounded-xl px-4 py-4 transition-all outline-none text-brand-secondary placeholder:text-neutral-text-secondary"
                  data-testid="cta-email-input"
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Phone number"
                  className="w-full bg-neutral-bg-secondary border-transparent focus:bg-white focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/20 rounded-xl px-4 py-4 transition-all outline-none text-brand-secondary placeholder:text-neutral-text-secondary"
                  data-testid="cta-phone-input"
                />
              </div>
              <div>
                <select
                  className="w-full bg-neutral-bg-secondary border-transparent focus:bg-white focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/20 rounded-xl px-4 py-4 transition-all outline-none text-neutral-text-secondary"
                  data-testid="cta-store-type-select"
                  defaultValue=""
                >
                  <option value="" disabled>Store type</option>
                  <option value="supermarket">Supermarket</option>
                  <option value="convenience">Convenience Store</option>
                  <option value="pharmacy">Pharmacy</option>
                  <option value="retail">Retail Chain</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full px-8 py-4 font-bold shadow-[0_4px_14px_0_rgba(99,91,255,0.39)] transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                data-testid="cta-submit-btn"
              >
                Start free trial
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            <p className="text-center text-neutral-text-secondary text-sm mt-6">
              No credit card required. By signing up, you agree to our{' '}
              <a href="#" className="text-brand-primary hover:underline">Terms</a>
              {' '}and{' '}
              <a href="#" className="text-brand-primary hover:underline">Privacy Policy</a>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
